<template>
	<div
		class="header-btn"
		:class="{ 'device-disabled': disabled, audio: isAudio }"
	>
		<span class="button-label" @click="toggleDevice()">
			<MicOnIcon class="device-icon audio-icon" v-if="isAudio && !disabled" />
			<MicOffIcon class="device-icon audio-icon" v-if="isAudio && disabled" />
			<CameraOnIcon
				class="device-icon video-icon"
				v-if="isVideo && !disabled"
			/>
			<CameraOffIcon
				class="device-icon video-icon"
				v-if="isVideo && disabled"
			/>
			<span v-text="typeLabel" />
		</span>
		<Menu
			@select="onSelectDevice"
			:items="deviceOptions"
			left
			menuClass="media-input-devices"
			ref="menu"
			@click.stop
		>
			<ChevronIcon
				class="chevron"
				:up="$refs.menu && $refs.menu.isOpen"
				:down="!$refs.menu || !$refs.menu.isOpen"
			/>
			<template #item="{ item }">
				<div
					class="device-option-item"
					:class="{ selected: item.selected, active: item.active }"
				>
					{{ item.label }}
					<CheckIcon />
				</div>
			</template>
		</Menu>
	</div>
</template>

<script>
import {
	store,
	getPublisher,
	getUserPrefs,
	getMyParticipant,
	getPublisherStream,
} from './data'
import { Menu } from '@/components/common'
import {
	CameraOnIcon,
	CameraOffIcon,
	MicOnIcon,
	MicOffIcon,
	ChevronIcon,
	CheckIcon,
} from '@/icons'
import { getMediaDevices, setPreferredDevice } from './data/common'

export default {
	components: {
		Menu,
		CameraOnIcon,
		CameraOffIcon,
		MicOnIcon,
		MicOffIcon,
		ChevronIcon,
		CheckIcon,
	},

	props: ['type'],

	data() {
		return {
			store,
			devices: null,
			disabled: false,
			lostAudio: false,
			lostVideo: false,
			showDevices: false,
			source: null,
			currentDevice: null,
			participant: getMyParticipant(),
			videoFilter: null,
		}
	},

	created() {
		this.$onEventBus({
			publisherMuted: () => {
				if (this.type === 'audio') {
					this.disabled = true
				}
			},
			lostAudioDevice: () => {
				if (this.isAudio) {
					this.lostAudio = true
					this.disabled = true
				}
			},
			lostVideoDevice: () => {
				if (this.isVideo) {
					this.lostVideo = true
					this.disabled = true
				}
			},
			publisherAdded: async () => {
				await this.getDevices()
				this.currentDevice = this.getCurrentDevice()
				const blurEnabled = localStorage.getItem('backgroundBlur')
				if (blurEnabled) {
					this.setBlur(blurEnabled)
				}
			},
		})
	},

	computed: {
		isAudio() {
			return this.type === 'audio'
		},

		isVideo() {
			return this.type === 'video'
		},

		typeLabel() {
			return this.$t(`accessibility_media_${this.type}`)
		},

		deviceStorageKey() {
			return `meetings_pref_device_${this.type}`
		},

		deviceOptions() {
			if (!this.devices?.length) return []
			const options = this.devices.map((device) => {
				const selected = device.deviceId === this.currentDevice?.deviceId
				return {
					id: device.deviceId,
					label: device.label,
					active: selected && !this.disabled,
					selected,
					device,
				}
			})
			const blurEnabled = localStorage.getItem('meetings_enableBackgroundBlur')
			if (blurEnabled) {
				const publisher = getPublisher()
				if (this.isVideo && publisher) {
					return [
						{ items: options },
						{
							divider: 'Background Blur',
							items: [
								{ id: 'blur-off', label: 'Off', selected: !this.videoFilter },
								{
									id: 'blur-low',
									label: 'Low',
									strength: 'low',
									selected: this.videoFilter === 'low',
								},
								{
									id: 'blur-high',
									label: 'High',
									strength: 'high',
									selected: this.videoFilter === 'high',
								},
							],
						},
					]
				}
			}
			return options
		},
	},

	mounted() {
		navigator.mediaDevices.addEventListener('devicechange', () => {
			this.getDevices()
		})

		this.$nextTick(() => {
			const { video, audio } = getUserPrefs()
			this.disabled = this.isAudio ? !audio : !video
		})

		if (this.isAudio) {
			this.$onEventBus({
				meetingUnmute: () => {
					this.unmute()
				},
				meetingMute: () => {
					this.mute()
				},
			})
		}
	},

	methods: {
		onSelectDevice({ id, strength, device }) {
			if (id?.startsWith('blur')) {
				return this.setBlur(strength)
			}
			this.enableDevice(device)
		},

		async getDevices() {
			this.devices = await getMediaDevices(this.type)
			return this.devices
		},

		getCurrentDevice() {
			const publisher = getPublisher()
			if (!publisher) return {}
			if (this.isAudio) {
				const track = publisher.getAudioSource()
				return { label: track.label, deviceId: track.getSettings().deviceId }
			} else {
				const { deviceId, track } = publisher.getVideoSource()
				return { label: track.label, deviceId }
			}
		},

		hideDevices() {
			this.showDevices = false
		},

		async enableDevice(device) {
			if (!device) return false
			this.showDevices = false

			const publisher = getPublisher()
			if (!publisher) return {}

			try {
				if (this.isAudio) {
					await publisher.setAudioSource(device.deviceId)
				} else {
					await publisher.setVideoSource(device.deviceId)
				}
				if (this.disabled) {
					this.currentDevice = {
						label: device.label,
						deviceId: device.deviceId,
					}
				} else {
					this.currentDevice = this.getCurrentDevice()
				}
				setPreferredDevice(this.type, device.deviceId)

				if (this.lostAudio || this.lostVideo) {
					this.lostAudio = false
					this.lostVideo = false
					this.disabled = false
				}

				this.isAudio
					? EventBus.$emit('dismissLostAudioAlert')
					: EventBus.$emit('dismissLostVideoAlert')
			} catch (e) {
				console.error(e)
				this.$alert.error()
			}
		},

		toggleDevice() {
			const stream = getPublisherStream()
			if (!stream) return
			if (this.isAudio) {
				stream.hasAudio ? this.mute() : this.unmute()
			} else if (this.isVideo) {
				const publisher = getPublisher()
				publisher.publishVideo(!stream.hasVideo)
				this.disabled = stream.hasVideo
			}
		},

		mute() {
			const publisher = getPublisher()
			publisher.publishAudio(false)
			this.disabled = true
		},

		unmute() {
			const publisher = getPublisher()
			publisher.publishAudio(true)
			this.disabled = false
		},

		async setBlur(blurStrength = false) {
			const publisher = getPublisher()
			if (!publisher || !publisher.stream) return
			try {
				if (!blurStrength) {
					await publisher.clearVideoFilter()
					localStorage.removeItem('backgroundBlur')
					this.videoFilter = null
				} else {
					await publisher.applyVideoFilter({
						type: 'backgroundBlur',
						blurStrength,
					})
					localStorage.setItem('backgroundBlur', blurStrength)
					this.videoFilter = blurStrength
				}
			} catch (e) {
				console.error(e)
				this.$alert.error(e)
			}
		},
	},
}
</script>

<style scoped lang="scss">
@import 'src/sass/variables';

$gray: #bcbcbc;

@media screen and (max-width: 1333px) {
	.button-label > span {
		display: none !important;
	}
}

.header-btn {
	color: var(--teal-300) !important;
	&.device-disabled {
		color: $gray !important;
		.device-icon {
			:deep(g) {
				fill: $gray;
			}
		}
	}
	.chevron {
		width: 27px;
		font-size: 20px;
		margin: 4px -10px 0 8px;
		padding: 0 8px;
		color: $gray !important;
		border-left: 1px solid #414141;
	}
	.device-icon {
		font-size: 14px;
		margin-right: 8px;
	}
	.button-label {
		@include flex;
	}
}

.device-option-item {
	@include flex-spread;
	width: 100%;
	color: var(--neutral-800);
	.pronto-icon {
		margin-left: 16px;
		opacity: 0;
	}
	&.selected {
		.pronto-icon {
			opacity: 1;
		}
	}
	&.active {
		color: var(--teal-300);
	}
	&:hover {
		&:not(.active) {
			color: var(--teal-200);
		}
	}
}

.point-down {
	transform: rotate(180deg);
}

.arrow {
	width: 10px;
	height: 10px;
	vertical-align: middle;
}

.device-icon {
	:deep(g) {
		fill: var(--teal-300);
	}
}

// these icons are all different sizes, so they
// have to be coerced into looking right
.audio-icon {
	width: 13px;
	height: 16px;
}
.video-icon {
	width: 19px;
	height: 16px;
}
</style>

<style lang="scss">
.menu.media-input-devices {
	margin-top: 4px;
	box-shadow: none !important;
	background-color: #38393c !important;
	.menu-section .text-divider-container .line {
		border-color: #353535;
	}
}
</style>
